import type { ReactNode } from 'react';
import { useState } from 'react';
import type { LayoutChangeEvent } from 'react-native';
import { Text, View } from 'react-native';
import Svg, { Defs, RadialGradient, Rect, Stop, SvgXml } from 'react-native-svg';
import { SCANS_LOGO_DARK_SVG, SCANS_LOGO_RATIO } from '~/assets/scansLogo';
import { useTheme } from '~/theme/ThemeProvider';

/** Logo width, from frame 00: 113 stacked above the form, 150 in the web column. */
const LOGO_W = { stacked: 113, column: 150 } as const;

/**
 * Height of the header row in the web column, from frame 00w's `208px auto 1fr auto`.
 *
 * The form column uses the same number, which is the whole point of it: it is what puts
 * "Sign in" on the same baseline as "Glass capture" across the fold.
 */
export const BRAND_HEADER_H = 208;

/**
 * The entry shell's brand panel: ink, a magenta wash, the wordmark and four lines saying
 * what this app is and who it is for.
 *
 * Two variants, because frame 00 draws it twice. `stacked` is the phone: a band above the
 * form, as tall as its content. `column` is the 1280 web split: a full-height half with
 * the logo in a fixed header row and the footer pinned to its bottom.
 *
 * The wash is an SVG radial gradient rather than a CSS one. React Native has no gradient
 * primitive, and the alternatives are worse: a PNG would not scale across the phone band
 * and the web column, and a stack of translucent views approximates a curve with bands
 * that show on an OLED phone in a dark workshop. `react-native-svg` is already a
 * dependency for the vehicle plan and the glyphs, and it draws the same gradient on web,
 * Android and iOS.
 *
 * The wordmark is the DARK variant. The light one is #69747C on black.
 */
export function BrandPanel({
  variant = 'stacked',
  washRadius = 480,
  footer,
}: {
  variant?: 'stacked' | 'column';
  /** Radius of the corner glow, in px. 480 on the phone band, 640 on the web column. */
  washRadius?: number;
  /** Pinned to the bottom of the panel. Only the column variant has room for one. */
  footer?: ReactNode;
}) {
  const { c, t, space } = useTheme();
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const column = variant === 'column';

  const onLayout = (e: LayoutChangeEvent) => {
    const { width, height } = e.nativeEvent.layout;
    setSize((prev) => (prev?.w === width && prev?.h === height ? prev : { w: width, h: height }));
  };

  const logoW = LOGO_W[variant];

  return (
    <View
      onLayout={onLayout}
      style={{
        backgroundColor: c.inverse,
        paddingHorizontal: space.lg,
        ...(column ? { flex: 1, paddingBottom: space.lg } : { paddingBottom: space.xl }),
      }}
    >
      {/*
        The wash is measured, not proportional. A gradient with `r="100%"` on a panel far
        wider than it is tall resolves to an ELLIPSE, which reads as a horizontal band
        across the bottom rather than as light from one corner. Laying it out in user space
        with a fixed radius keeps it a circle at every width, which is what the design
        draws — so the panel's own size has to be known before it can be drawn, and on the
        first frame it simply is not there.
      */}
      {size ? (
        <Svg
          style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
          width={size.w}
          height={size.h}
          pointerEvents="none"
        >
          <Defs>
            <RadialGradient
              id="brandWash"
              gradientUnits="userSpaceOnUse"
              cx={0}
              cy={size.h}
              r={washRadius}
            >
              <Stop offset="0" stopColor={c.brandPrimary} stopOpacity={0.24} />
              <Stop offset="0.7" stopColor={c.brandPrimary} stopOpacity={0} />
            </RadialGradient>
          </Defs>
          <Rect x={0} y={0} width={size.w} height={size.h} fill="url(#brandWash)" />
        </Svg>
      ) : null}

      {column ? (
        <View style={{ height: BRAND_HEADER_H, paddingTop: space.lg }}>
          <SvgXml xml={SCANS_LOGO_DARK_SVG} width={logoW} height={logoW / SCANS_LOGO_RATIO} />
        </View>
      ) : (
        <SvgXml
          xml={SCANS_LOGO_DARK_SVG}
          width={logoW}
          height={logoW / SCANS_LOGO_RATIO}
          style={{ marginTop: space.md, marginBottom: space.xl }}
        />
      )}

      <View style={{ gap: space.sm, maxWidth: 440 }}>
        <Text style={[t.monoEyebrow, { color: c.brandOnInverse, textTransform: 'uppercase' }]}>
          Glass capture
        </Text>
        <Text style={[t.h1, { color: c.onInverse }]}>Field console</Text>
        <Text style={[t.bodyLg, { color: c.onInverse, lineHeight: 24 }]}>
          Photograph the glass. Read the verdict.
        </Text>
        <Text style={[t.body, { color: c.onInverseMuted, lineHeight: 22 }]}>
          For operators at the vehicle: scan the VIN, photograph each claimed pane, send.
        </Text>
      </View>

      {column ? (
        <>
          <View style={{ flex: 1 }} />
          {footer}
        </>
      ) : null}
    </View>
  );
}
